/**
 * FormFillerWizardPage
 *
 * Multi-step wizard for the Form Filler feature:
 * Step 1: Upload a blank/partially-filled form
 * Step 2: Review and edit AI-suggested field values
 * Step 3: Download the filled form
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useCaseFile } from '../contexts/CaseFileContext';
import { Sidebar, Header } from '../components/dashboard';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Check,
  FileText,
  Loader2,
  AlertCircle,
  Download,
  CheckCircle,
  X,
  Info,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  formFillerService,
  type AnalyzedField,
  type FieldConfidence,
  type ReviewedField,
  type AnalyzeFormResponse,
  type GenerateFilledFormResponse,
} from '../services/formFillerService';

// Progress messages for form analysis
const ANALYSIS_PROGRESS_MESSAGES = [
  { delay: 0, message: 'Uploading form...' },
  { delay: 3000, message: 'Analyzing form structure...' },
  { delay: 8000, message: 'Identifying fields...' },
  { delay: 15000, message: 'Matching to your care record data...' },
  { delay: 25000, message: 'Mapping field values...' },
  { delay: 40000, message: 'Almost done...' },
];

// Accepted file types
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB for forms

// Wizard steps
type WizardStep = 'upload' | 'review' | 'download';
const STEPS: Array<{ id: WizardStep; label: string }> = [
  { id: 'upload', label: 'Upload Form' },
  { id: 'review', label: 'Review Fields' },
  { id: 'download', label: 'Download' },
];

// Confidence badge styles
const CONFIDENCE_STYLES: Record<FieldConfidence, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-green-100', text: 'text-green-700', label: 'High' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Medium' },
  low: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Low' },
  unmapped: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Unmapped' },
};

interface SignedUrlResponse {
  signedUrl: string;
  documentId: string;
  filePath: string;
  expiresAt: string;
}

export function FormFillerWizardPage(): JSX.Element {
  const { caseFileId: urlCaseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();
  const { caseFiles, activeCaseFileId, setActiveCaseFileId, refreshCaseFiles } = useCaseFile();
  const caseFileId = urlCaseFileId || activeCaseFileId;

  useEffect(() => {
    if (urlCaseFileId && urlCaseFileId !== activeCaseFileId) {
      setActiveCaseFileId(urlCaseFileId);
    }
  }, [urlCaseFileId, activeCaseFileId, setActiveCaseFileId]);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');

  // Step 1: Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [userNotes, setUserNotes] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgressMessage, setAnalysisProgressMessage] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Review state
  const [formAnalysisId, setFormAnalysisId] = useState<string | null>(null);
  const [formStoragePath, setFormStoragePath] = useState<string | null>(null);
  const [formFileName, setFormFileName] = useState<string>('');
  const [formPageCount, setFormPageCount] = useState(0);
  const [fields, setFields] = useState<AnalyzedField[]>([]);
  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map());
  const [skippedFields, setSkippedFields] = useState<Set<string>>(new Set());
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Step 3: Download state
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const [fillMethod, setFillMethod] = useState<string | null>(null);

  // Progress message timer for analysis
  useEffect(() => {
    if (!isAnalyzing) return;

    const timers: NodeJS.Timeout[] = [];
    for (const { delay, message } of ANALYSIS_PROGRESS_MESSAGES) {
      timers.push(setTimeout(() => setAnalysisProgressMessage(message), delay));
    }

    return () => timers.forEach(clearTimeout);
  }, [isAnalyzing]);

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Unsupported file type. Please upload a PDF, JPEG, PNG, or TIFF.';
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    setSelectedFile(file);
    setUploadError(null);
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Upload and analyze form
  const handleAnalyzeForm = async () => {
    if (!selectedFile || !caseFileId) return;

    setIsAnalyzing(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Get signed URL for upload
      setUploadProgress(10);
      const getSignedUrl = httpsCallable<
        { caseFileId: string; fileName: string; contentType: string },
        SignedUrlResponse
      >(functions, 'generateSignedUrl');

      const urlResult = await getSignedUrl({
        caseFileId,
        fileName: selectedFile.name,
        contentType: selectedFile.type,
      });

      if (!urlResult.data.signedUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Step 2: Upload file to Cloud Storage
      setUploadProgress(30);
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = 30 + Math.round((event.loaded / event.total) * 30);
          setUploadProgress(progress);
        }
      };

      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', urlResult.data.signedUrl);
        xhr.setRequestHeader('Content-Type', selectedFile.type);
        xhr.send(selectedFile);
      });

      setUploadProgress(65);
      const storagePath = urlResult.data.filePath;

      // Step 3: Call analyzeForm API
      setUploadProgress(70);
      const analysisResult: AnalyzeFormResponse = await formFillerService.analyzeForm({
        caseFileId,
        formStoragePath: storagePath,
        formFileName: selectedFile.name,
        userNotes: userNotes || undefined,
      });

      if (!analysisResult.success || !analysisResult.fields) {
        throw new Error(analysisResult.error || 'Form analysis failed');
      }

      setUploadProgress(100);

      // Store results and move to review step
      setFormAnalysisId(analysisResult.formAnalysisId || null);
      setFormStoragePath(storagePath);
      setFormFileName(analysisResult.formFileName || selectedFile.name);
      setFormPageCount(analysisResult.formPageCount || 1);
      setFields(analysisResult.fields);

      // Initialize edited values with suggested values
      const initialValues = new Map<string, string>();
      for (const field of analysisResult.fields) {
        initialValues.set(field.fieldId, field.suggestedValue || '');
      }
      setEditedValues(initialValues);

      setCurrentStep('review');
    } catch (err) {
      console.error('Form analysis error:', err);
      setUploadError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setIsAnalyzing(false);
      setUploadProgress(0);
    }
  };

  // Generate filled form
  const handleGenerateFilledForm = async () => {
    if (!formAnalysisId || !formStoragePath || !caseFileId) return;

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const reviewedFields: ReviewedField[] = fields.map((field) => ({
        fieldId: field.fieldId,
        label: field.label,
        finalValue: editedValues.get(field.fieldId) || '',
        fieldType: field.fieldType,
        skip: skippedFields.has(field.fieldId),
      }));

      const result: GenerateFilledFormResponse = await formFillerService.generateFilledForm({
        caseFileId,
        formAnalysisId,
        formStoragePath,
        fields: reviewedFields,
        outputFileName: formFileName,
      });

      if (!result.success || !result.downloadUrl) {
        throw new Error(result.error || 'Failed to generate filled form');
      }

      setDownloadUrl(result.downloadUrl);
      setDocId(result.docId || null);
      setFillMethod(result.fillMethod || null);
      setCurrentStep('download');
    } catch (err) {
      console.error('Generate filled form error:', err);
      setGenerateError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Field editing helpers
  const handleFieldValueChange = (fieldId: string, value: string) => {
    setEditedValues((prev) => {
      const next = new Map(prev);
      next.set(fieldId, value);
      return next;
    });
  };

  const toggleSkipField = (fieldId: string) => {
    setSkippedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  // Computed values
  const filledCount = fields.filter(
    (f) => !skippedFields.has(f.fieldId) && (editedValues.get(f.fieldId) || '').length > 0
  ).length;
  const skippedCount = skippedFields.size;
  const totalCount = fields.length;

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);

  // Filter fields for display
  const displayFields = showUnmappedOnly
    ? fields.filter((f) => f.confidence === 'unmapped')
    : fields;

  // Group fields by section
  const fieldsBySection = new Map<string, AnalyzedField[]>();
  for (const field of displayFields) {
    const section = field.section || 'General';
    if (!fieldsBySection.has(section)) {
      fieldsBySection.set(section, []);
    }
    fieldsBySection.get(section)!.push(field);
  }

  if (!caseFileId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">No Care Record Selected</h2>
          <p className="text-muted mb-6">Please select a care record to fill forms.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <Header
          caseFiles={caseFiles.map((cf) => ({ id: cf.caseFileId, name: cf.name }))}
          activeCaseFileId={caseFileId}
          onCaseFileChange={(id) => {
            setActiveCaseFileId(id);
            navigate(`/casefile/${id}/casemaker/form-filler`);
          }}
          onMenuClick={() => setSidebarOpen(true)}
          showMobileMenu={true}
          onJoinedCareTeam={(id) => {
            setActiveCaseFileId(id);
            refreshCaseFiles();
            navigate(`/casefile/${id}/casemaker/form-filler`);
          }}
        />

        {/* Page Header */}
        <div className="bg-white border-b border-soft-neutral px-4 lg:px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/casefile/${caseFileId}/casemaker`)}
              className="btn-ghost btn-sm flex items-center gap-1"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back to CaseMaker</span>
            </button>
            <div className="h-6 w-px bg-soft-neutral" />
            <h1 className="text-xl font-semibold text-ink">Fill a Form</h1>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white border-b border-soft-neutral px-4 lg:px-6 py-3">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center gap-2 flex-1">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      index < currentStepIndex
                        ? 'bg-evergreen text-white'
                        : index === currentStepIndex
                        ? 'bg-evergreen/10 text-evergreen border-2 border-evergreen'
                        : 'bg-gray-100 text-muted'
                    }`}
                  >
                    {index < currentStepIndex ? <Check size={16} /> : index + 1}
                  </div>
                  <span
                    className={`text-sm hidden sm:inline ${
                      index === currentStepIndex ? 'font-medium text-ink' : 'text-muted'
                    }`}
                  >
                    {step.label}
                  </span>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-px mx-2 ${
                        index < currentStepIndex ? 'bg-evergreen' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-4 lg:p-6">
          <div className="max-w-3xl mx-auto">
            {/* ============================================================ */}
            {/* STEP 1: UPLOAD FORM */}
            {/* ============================================================ */}
            {currentStep === 'upload' && (
              <div className="space-y-6">
                <div className="card">
                  <div className="card-body">
                    <h2 className="text-lg font-semibold text-ink mb-1">Upload Your Form</h2>
                    <p className="text-muted text-sm mb-6">
                      Upload a blank or partially-filled form and we'll identify the fields and auto-fill
                      them with data from your care record.
                    </p>

                    {/* File Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        isDragging
                          ? 'border-evergreen bg-evergreen/5'
                          : selectedFile
                          ? 'border-evergreen/50 bg-green-50'
                          : 'border-gray-300 hover:border-evergreen/50 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />

                      {selectedFile ? (
                        <div className="flex flex-col items-center gap-2">
                          <CheckCircle className="text-evergreen" size={40} />
                          <p className="font-medium text-ink">{selectedFile.name}</p>
                          <p className="text-sm text-muted">
                            {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(null);
                            }}
                            className="btn-ghost btn-sm text-muted mt-2"
                          >
                            <X size={14} className="mr-1" />
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="text-muted" size={40} />
                          <p className="font-medium text-ink">
                            Drop your form here or click to browse
                          </p>
                          <p className="text-sm text-muted">
                            Supports PDF, JPEG, PNG, TIFF (max 20MB)
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Optional Notes */}
                    <div className="mt-6">
                      <label className="label text-sm" htmlFor="userNotes">
                        Notes (optional)
                      </label>
                      <textarea
                        id="userNotes"
                        value={userNotes}
                        onChange={(e) => setUserNotes(e.target.value)}
                        placeholder="e.g., This is a Medicaid renewal for 2026"
                        className="input w-full"
                        rows={2}
                      />
                    </div>

                    {/* Error */}
                    {uploadError && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                        <p className="text-sm text-red-700">{uploadError}</p>
                      </div>
                    )}

                    {/* Progress */}
                    {isAnalyzing && (
                      <div className="mt-6 space-y-3">
                        <div className="flex items-center gap-3">
                          <Loader2 className="animate-spin text-evergreen" size={20} />
                          <p className="text-sm text-ink font-medium">
                            {analysisProgressMessage || 'Starting...'}
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-evergreen h-2 rounded-full transition-all duration-500"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Analyze Button */}
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={handleAnalyzeForm}
                        disabled={!selectedFile || isAnalyzing}
                        className="btn-primary flex items-center gap-2"
                      >
                        {isAnalyzing ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <FileText size={18} />
                        )}
                        {isAnalyzing ? 'Analyzing...' : 'Analyze Form'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <div className="card bg-blue-50 border-blue-200">
                  <div className="card-body">
                    <div className="flex items-start gap-3">
                      <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="text-sm font-medium text-blue-800">What forms can I fill?</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Medicaid renewals, SSI applications, school enrollment forms, insurance
                          claims, and more. Upload any typed or printed form and we'll extract the
                          fields.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* STEP 2: REVIEW & EDIT FIELDS */}
            {/* ============================================================ */}
            {currentStep === 'review' && (
              <div className="space-y-4">
                {/* Summary Bar */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-ink">Review Fields</h2>
                        <p className="text-sm text-muted">
                          {formFileName} ({formPageCount} page{formPageCount !== 1 ? 's' : ''})
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-evergreen" />
                          <span className="text-ink font-medium">{filledCount}</span>
                          <span className="text-muted">filled</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                          <span className="text-ink font-medium">{skippedCount}</span>
                          <span className="text-muted">skipped</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-orange-300" />
                          <span className="text-ink font-medium">
                            {fields.filter((f) => f.confidence === 'unmapped').length}
                          </span>
                          <span className="text-muted">unmapped</span>
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-muted mb-1">
                        <span>Auto-fill coverage</span>
                        <span>{totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-evergreen h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${totalCount > 0 ? (filledCount / totalCount) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filter Toggle */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
                    className={`btn-ghost btn-sm flex items-center gap-2 ${
                      showUnmappedOnly ? 'text-orange-600' : 'text-muted'
                    }`}
                  >
                    {showUnmappedOnly ? <Eye size={16} /> : <EyeOff size={16} />}
                    {showUnmappedOnly ? 'Showing unmapped only' : 'Show unmapped only'}
                  </button>
                  <span className="text-sm text-muted">
                    {displayFields.length} of {totalCount} fields
                  </span>
                </div>

                {/* Fields by Section */}
                {Array.from(fieldsBySection.entries()).map(([section, sectionFields]) => (
                  <div key={section} className="space-y-2">
                    <h3 className="text-sm font-semibold text-ink px-1 pt-2">{section}</h3>
                    {sectionFields.map((field) => {
                      const isSkipped = skippedFields.has(field.fieldId);
                      const currentValue = editedValues.get(field.fieldId) || '';
                      const confidenceStyle = CONFIDENCE_STYLES[field.confidence];

                      return (
                        <div
                          key={field.fieldId}
                          className={`card transition-opacity ${isSkipped ? 'opacity-50' : ''}`}
                        >
                          <div className="card-body py-3 px-4">
                            <div className="flex items-start gap-3">
                              {/* Field content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                  <span className="text-sm font-medium text-ink">
                                    {field.label}
                                  </span>
                                  <span
                                    className={`badge badge-sm ${confidenceStyle.bg} ${confidenceStyle.text}`}
                                  >
                                    {confidenceStyle.label}
                                  </span>
                                  {field.existingValue && (
                                    <span className="badge badge-sm bg-blue-100 text-blue-700">
                                      Has existing value
                                    </span>
                                  )}
                                </div>

                                {field.dataSource && !isSkipped && (
                                  <p className="text-xs text-muted mb-2">
                                    Source: {field.dataSource}
                                  </p>
                                )}

                                {/* Input based on field type */}
                                {!isSkipped && (
                                  <>
                                    {field.fieldType === 'checkbox' ? (
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={currentValue === 'checked'}
                                          onChange={(e) =>
                                            handleFieldValueChange(
                                              field.fieldId,
                                              e.target.checked ? 'checked' : 'unchecked'
                                            )
                                          }
                                          className="rounded border-gray-300 text-evergreen focus:ring-evergreen"
                                        />
                                        <span className="text-sm text-muted">
                                          {currentValue === 'checked' ? 'Checked' : 'Unchecked'}
                                        </span>
                                      </div>
                                    ) : field.fieldType === 'textarea' ? (
                                      <textarea
                                        value={currentValue}
                                        onChange={(e) =>
                                          handleFieldValueChange(field.fieldId, e.target.value)
                                        }
                                        className="input w-full text-sm"
                                        rows={2}
                                        placeholder={
                                          field.confidence === 'unmapped'
                                            ? 'Enter value manually...'
                                            : ''
                                        }
                                      />
                                    ) : (
                                      <input
                                        type={field.fieldType === 'date' ? 'text' : 'text'}
                                        value={currentValue}
                                        onChange={(e) =>
                                          handleFieldValueChange(field.fieldId, e.target.value)
                                        }
                                        className="input w-full text-sm"
                                        placeholder={
                                          field.confidence === 'unmapped'
                                            ? 'Enter value manually...'
                                            : ''
                                        }
                                      />
                                    )}
                                  </>
                                )}

                                {isSkipped && (
                                  <p className="text-sm text-muted italic">
                                    Field will be left blank
                                  </p>
                                )}
                              </div>

                              {/* Skip toggle */}
                              <button
                                onClick={() => toggleSkipField(field.fieldId)}
                                className={`btn-ghost btn-sm flex-shrink-0 ${
                                  isSkipped ? 'text-orange-600' : 'text-muted'
                                }`}
                                title={isSkipped ? 'Include this field' : 'Skip this field'}
                              >
                                {isSkipped ? 'Include' : 'Skip'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Error */}
                {generateError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-red-700">{generateError}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4">
                  <button
                    onClick={() => {
                      setCurrentStep('upload');
                      setFields([]);
                      setFormAnalysisId(null);
                      setSelectedFile(null);
                    }}
                    className="btn-ghost flex items-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Start Over
                  </button>
                  <button
                    onClick={handleGenerateFilledForm}
                    disabled={isGenerating || filledCount === 0}
                    className="btn-primary flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <ArrowRight size={18} />
                        Generate Filled Form
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* STEP 3: DOWNLOAD */}
            {/* ============================================================ */}
            {currentStep === 'download' && (
              <div className="space-y-6">
                <div className="card">
                  <div className="card-body text-center py-12">
                    <CheckCircle className="mx-auto text-evergreen mb-4" size={56} />
                    <h2 className="text-xl font-semibold text-ink mb-2">Form Filled Successfully</h2>
                    <p className="text-muted mb-1">
                      {filledCount} of {totalCount} fields were filled
                      {skippedCount > 0 && ` (${skippedCount} skipped)`}
                    </p>
                    {fillMethod === 'companion' && (
                      <p className="text-sm text-muted mb-6">
                        A companion document was generated with all your field values. You can
                        reference it while filling the original form.
                      </p>
                    )}
                    {fillMethod === 'acroform' && (
                      <p className="text-sm text-muted mb-6">
                        The form was filled directly with your data and flattened for security.
                      </p>
                    )}

                    {downloadUrl && (
                      <a
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-3"
                      >
                        <Download size={20} />
                        Download Filled Form
                      </a>
                    )}

                    <p className="text-xs text-muted mt-4">
                      Download link expires in 1 hour.
                      {docId && (
                        <>
                          {' '}
                          <button
                            onClick={() => navigate(`/casefile/${caseFileId}/casemaker/${docId}`)}
                            className="text-evergreen underline"
                          >
                            View in CaseMaker
                          </button>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      // Reset and start over
                      setCurrentStep('upload');
                      setFields([]);
                      setFormAnalysisId(null);
                      setFormStoragePath(null);
                      setSelectedFile(null);
                      setEditedValues(new Map());
                      setSkippedFields(new Set());
                      setDownloadUrl(null);
                      setDocId(null);
                    }}
                    className="btn-ghost flex items-center gap-2"
                  >
                    <FileText size={16} />
                    Fill Another Form
                  </button>
                  <button
                    onClick={() => navigate(`/casefile/${caseFileId}/casemaker`)}
                    className="btn-ghost flex items-center gap-2"
                  >
                    Back to CaseMaker
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormFillerWizardPage;
